import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock } from "lucide-react";
import { formatBytes, formatDuration } from "@/lib/api";

/**
 * RecentMigrationsPulse — horizontal strip of the last N successful
 * migrations rendered as pill chips with size + duration.
 *
 * Acts as a quiet social-proof bar at the top of each migrations page
 * ("3.2 TB · 41 min · ✓") and as a one-click shortcut into the most
 * recent successful jobs without scrolling the full table.
 *
 * Pure presentational — the parent passes already-fetched items.
 * Self-hides when there's nothing to show so it never shouts at
 * empty-state pages.
 */
export default function RecentMigrationsPulse({ items, label = "Recently completed", testId }) {
    if (!items || items.length === 0) return null;

    return (
        <div
            data-testid={testId || "recent-migrations-pulse"}
            className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface/60 px-3 py-2 shadow-sm"
        >
            <div className="flex items-center gap-1.5 pr-1">
                <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {label}
                </span>
            </div>
            {items.map((it) => (
                <PulseChip key={it.id} item={it} />
            ))}
        </div>
    );
}

function PulseChip({ item }) {
    const inner = (
        <span
            data-testid={`pulse-chip-${item.id}`}
            className="group inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/[0.06] px-2.5 py-1 transition-all hover:-translate-y-0.5 hover:border-emerald-500/50 hover:bg-emerald-500/[0.12]"
            title={item.completedAt ? `Completed ${new Date(item.completedAt).toLocaleString()}` : undefined}
        >
            <CheckCircle2 size={11} className="shrink-0 text-emerald-400" />
            <span className="max-w-[160px] truncate font-mono text-[11px] font-semibold text-foreground">
                {item.name}
            </span>
            {item.bytes != null && item.bytes > 0 && (
                <span className="font-mono text-[10px] text-muted-foreground">
                    · {formatBytes(item.bytes)}
                </span>
            )}
            {item.durationMs != null && item.durationMs > 0 && (
                <span className="inline-flex items-center gap-0.5 font-mono text-[10px] text-muted-foreground">
                    <Clock size={9} /> {formatDuration(item.durationMs)}
                </span>
            )}
        </span>
    );
    return item.href ? (
        <Link to={item.href} className="no-underline">{inner}</Link>
    ) : inner;
}

/* ---------- Source-specific adapters ----------------------------------
 * Each migrations page has a different shape; rather than make the
 * component aware of all three, we expose tiny helper functions the
 * page can apply to its already-fetched list before passing it in.
 * Keeps the component dumb and the page in full control of data flow.
 * -------------------------------------------------------------------- */

const safeDelta = (start, end) => {
    if (!start || !end) return 0;
    return Math.max(0, new Date(end).getTime() - new Date(start).getTime());
};

/** PC captures from GET /api/migrations. */
export function adaptCaptures(captures = [], limit = 3) {
    return (captures || [])
        .filter((c) => c.status === "completed")
        .sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""))
        .slice(0, limit)
        .map((c) => ({
            id: c.id,
            name: c.source_agent_name || (c.source_agent_id || "").slice(0, 8) || "capture",
            bytes: c.total_bytes || 0,
            durationMs: safeDelta(c.created_at, c.completed_at),
            href: null,
            completedAt: c.completed_at,
        }));
}

/** Server jobs from GET /api/jobs (already filtered to migration_kind=server). */
export function adaptServerJobs(jobs = [], limit = 3) {
    return (jobs || [])
        .filter((j) => {
            const s = (j.status || "").toLowerCase();
            return s === "succeeded" || s === "completed" || s === "verified";
        })
        .sort((a, b) => (b.completed_at || b.updated_at || "").localeCompare(a.completed_at || a.updated_at || ""))
        .slice(0, limit)
        .map((j) => ({
            id: j.id,
            name: j.name || (j.id || "").slice(0, 8),
            bytes: j.bytes_done || j.total_bytes || 0,
            durationMs: safeDelta(j.started_at || j.created_at, j.completed_at),
            href: `/jobs/${j.id}`,
            completedAt: j.completed_at,
        }));
}

/** Data migrations from GET /api/storage-migrations. */
export function adaptNasMigrations(items = [], limit = 3) {
    return (items || [])
        .filter((m) => {
            const s = (m.status || "").toLowerCase();
            return s === "completed" || s === "verified";
        })
        .sort((a, b) => (b.completed_at || b.updated_at || "").localeCompare(a.completed_at || a.updated_at || ""))
        .slice(0, limit)
        .map((m) => ({
            id: m.id,
            name: m.name || (m.id || "").slice(0, 8),
            bytes: m.bytes_transferred || m.total_bytes || 0,
            durationMs: safeDelta(m.started_at || m.created_at, m.completed_at),
            href: `/migrate/storage/${m.id}`,
            completedAt: m.completed_at,
        }));
}
